import {
  PurchaseOrderStatus,
  enumPurchaseOrderSortOption,
} from '@rematter/frontend-lib-api-client';

import { createEntityComboBox } from './helpers/createEntityComboBox';

import { createQueryHook } from '~/shared/providers/api';
import { QueryHookResult } from '~/shared/providers/api/createQueryHook';

export type SelectablePurchaseOrder = QueryHookResult<
  typeof usePurchaseOrdersList
>[number];

export const usePurchaseOrdersList = createQueryHook(
  'shared.selectPurchaseOrder',
  (params: {
    ids?: string[];
    searchTerm?: string;
    facilityIds?: string[];
    accountIds?: string[];
    statuses?: PurchaseOrderStatus[];
  }) => ({
    purchaseOrders: {
      __args: {
        filter: {
          ids: params.ids,
          searchTerm: params.searchTerm,
          facilityIds: params.facilityIds,
          accountIds: params.accountIds,
          status: params.statuses,
        },
        sorting: [enumPurchaseOrderSortOption.CreatedAtDesc],
        pagination: {
          limit: 20,
          offset: 0,
        },
      },
      entries: {
        id: true,
        documentNumber: true,
        status: true,
        counterpartyReferenceNumber: true,
        account: {
          id: true,
          name: true,
        },
        startDate: true,
        endDate: true,
      },
    },
  }),
  {
    select: (data) => data.purchaseOrders.entries ?? [],
  },
);

const formatPurchaseOrderLabel = (entity: SelectablePurchaseOrder): string => {
  const parts = [`#${entity.documentNumber}`];
  if (entity.account?.name) {
    parts.push(`- ${entity.account.name}`);
  }
  return parts.join(' ');
};

const formatPurchaseOrderSubLabel = (
  entity: SelectablePurchaseOrder,
): string => {
  const parts: string[] = [];
  if (entity.counterpartyReferenceNumber) {
    parts.push(`Ref: ${entity.counterpartyReferenceNumber}`);
  }
  parts.push(entity.status);
  return parts.join(' • ');
};

export const SelectPurchaseOrder = createEntityComboBox.remotelySearched(
  'SelectPurchaseOrder',
  usePurchaseOrdersList,
  {
    formatLabel: formatPurchaseOrderLabel,
    formatSubLabel: formatPurchaseOrderSubLabel,
    compactSelection: true,
  },
);
